import { useState } from 'react'
import { MessageSquarePlus } from 'lucide-react'
import { leadsAPI } from '../api'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../../i18n/apiError'

const STATUS_OPTIONS = [
  { value: '', labelKey: 'followUp.noChange' },
  { value: 'ny', labelKey: 'followUp.status.ny' },
  { value: 'kontaktet', labelKey: 'followUp.status.kontaktet' },
  { value: 'tilbud_sendt', labelKey: 'followUp.status.tilbudSendt' },
  { value: 'ikke_aktuell', labelKey: 'followUp.status.ikkeAktuell' },
  { value: 'vunnet', labelKey: 'followUp.status.vunnet' },
] as const

interface Props {
  orgNr: string
  onFollowUpCreated: () => void
}

/** The add-activity form. The full log is rendered by Aktiviteter (#389). */
export default function FollowUpSection({ orgNr, onFollowUpCreated }: Props) {
  const { t } = useTranslation('leads')
  const [comment, setComment] = useState('')
  const [status, setStatus] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!comment.trim()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await leadsAPI.createFollowUp({
        target_org_nr: orgNr,
        comment: comment.trim(),
        status: status || undefined,
      })
      setComment('')
      setStatus('')
      onFollowUpCreated()
    } catch (err: any) {
      setSubmitError(localizeApiDetail(err.response?.data?.detail) || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="card p-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          <MessageSquarePlus className="w-5 h-5 inline mr-2" />{t('followUp.title')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('followUp.statusLabel')}</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="input-field text-sm w-full">
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('followUp.comment')}</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              className="input-field text-sm w-full resize-none"
              placeholder={t('followUp.placeholder')}
            />
          </div>
          {submitError && (
            <div className="text-red-600 dark:text-red-400 text-xs">{submitError}</div>
          )}
          <button
            type="submit"
            disabled={submitting || !comment.trim()}
            className="w-full px-4 py-2 rounded-sm bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40 transition-colors"
          >
            {submitting ? t('followUp.saving') : t('followUp.save')}
          </button>
        </form>
    </div>
  )
}
